import { Reveal } from "@/components/Reveal";
import { Link } from "react-router-dom";
import { MERCH_STORE_URL } from "@/assets";
import { useContent } from "@/components/useContent";
import { ShieldCheck, AudioWaveform, GraduationCap, Globe2, ArrowRight, ShoppingBag } from "lucide-react";

export const PILLARS = [
  {
    icon: ShieldCheck,
    title: "Artist Autonomy & Catalog Ownership",
    body: "We prioritize independent deal structures, intellectual property protection, and sustainable business models that keep real power in the hands of the creators.",
  },
  {
    icon: AudioWaveform,
    title: "The Signature Sound",
    body: "Pioneering Cold-Blooded Soundscapes™ — a distinct genre-blending fusion of melodic trap, trap-soul, R&B, and raw Southern hip-hop storytelling.",
  },
  {
    icon: GraduationCap,
    title: "End-to-End Development",
    body: "From vocal craft and audio engineering to release strategies and stage performance, our mentorship model turns raw talent into seasoned industry professionals.",
  },
  {
    icon: Globe2,
    title: "The Extended Ecosystem",
    body: "More than a record label. Through our official retail store, Boa's 33rd Dimension™, we bridge music with culture via custom apparel lines (Cold-Blooded Culture Attire™, Slither N Sparkle™), custom footwear (Slitherin' Soles™), and high-caliber digital beat licensing.",
  },
];

export const STATS = [
  { value: "#1", label: "Local & Regional — ReverbNation Charts" },
  { value: "100%", label: "Independent Catalog Ownership" },
  { value: "2", label: "Visionary Founders & Executive Strategists" },
  { value: "1", label: "Trademarked Signature Sound" },
];

export const AboutHeadline = ({ compact = false }) => (
  <Reveal className={compact ? "max-w-3xl" : "max-w-4xl"}>
    <p className="font-mono text-xs uppercase tracking-[0.25em] text-venom font-medium mb-4">
      About the Label
    </p>
    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight" data-testid="about-headline">
      Raw storytelling.
      <br />
      Bulletproof business.
      <br />
      <span className="text-stroke-ice">Uncompromised ownership.</span>
    </h2>
    <p className="mt-5 text-sm sm:text-base text-crystal font-medium">
      Welcome to DGE: Cold-Blooded Records LLC™ — Home of Cold-Blooded Soundscapes™.
    </p>
  </Reveal>
);

export const PillarsGrid = () => (
  <div className="grid sm:grid-cols-2 sm:auto-rows-fr gap-5 lg:gap-6" data-testid="about-pillars">
    {PILLARS.map((p, i) => {
      const Icon = p.icon;
      return (
        <Reveal key={p.title} delay={i * 0.08} className="h-full">
          <div
            className="group h-full bg-charcoal border border-crystal/20 rounded-2xl p-6 sm:p-7 hover:border-venom/50 hover:-translate-y-1 hover:shadow-[0_10px_40px_rgba(57,255,20,0.12)] transition-all duration-400"
            data-testid={`pillar-card-${i}`}
          >
            <div className="w-11 h-11 rounded-xl bg-crystal/15 border border-crystal/40 flex items-center justify-center mb-5 group-hover:bg-venom/15 group-hover:border-venom/50 transition-colors duration-500">
              <Icon size={20} className="text-venom" />
            </div>
            <h3 className="font-display text-white text-base sm:text-lg font-bold">{p.title}</h3>
            <p className="mt-2.5 text-sm text-ice/65 leading-relaxed">{p.body}</p>
          </div>
        </Reveal>
      );
    })}
  </div>
);

export const StatsBanner = () => (
  <Reveal>
    <div
      className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 border-y border-crystal/25 bg-charcoal/50 rounded-2xl px-6 sm:px-10 py-8 sm:py-10"
      data-testid="stats-banner"
    >
      {STATS.map((s, i) => (
        <div key={s.label} className="text-center" data-testid={`stat-item-${i}`}>
          <p className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-venom drop-shadow-[0_0_18px_rgba(57,255,20,0.3)]">
            {s.value}
          </p>
          <p className="mt-2 text-[11px] sm:text-xs text-ice/60 leading-snug max-w-[180px] mx-auto">{s.label}</p>
        </div>
      ))}
    </div>
  </Reveal>
);

export const AboutCTA = () => (
  <Reveal>
    <div className="text-center" data-testid="about-cta">
      <h3 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-white tracking-tight">
        Ready to Experience the Movement?
      </h3>
      <div className="mt-7 flex flex-wrap items-center justify-center gap-4">
        <Link
          to="/roster"
          className="group inline-flex items-center gap-2.5 bg-venom text-obsidian font-bold text-sm sm:text-base px-8 py-4 rounded-full hover:glow-venom hover:scale-[1.03] active:scale-95 transition-all duration-300"
          data-testid="about-cta-roster"
        >
          Explore Our Roster &amp; Releases
          <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
        <a
          href={MERCH_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2.5 border border-crystal/60 text-ice font-semibold text-sm sm:text-base px-8 py-4 rounded-full hover:bg-crystal/15 hover:border-crystal hover:glow-crystal active:scale-95 transition-all duration-300"
          data-testid="about-cta-store"
        >
          <ShoppingBag size={17} className="text-venom" />
          Shop Boa&apos;s 33rd Dimension™ Store
        </a>
      </div>
    </div>
  </Reveal>
);

export const HomeAbout = () => {
  const content = useContent();
  return (
  <section className="relative py-20 lg:py-28" data-testid="home-about-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-16 items-start">
        <div className="lg:sticky lg:top-28">
          <AboutHeadline compact />
          <Reveal delay={0.15}>
            <p className="mt-6 text-sm sm:text-base text-ice/70 leading-relaxed">
              {content.home_about_excerpt || "DGE: Cold-Blooded Records LLC™ (Dirt Gang Entertainment) is an independent music powerhouse and lifestyle enterprise built on one radical premise: real artists should own their craft, control their catalogs, and dictate their legacy."}
            </p>
            <Link
              to="/about"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-venom hover:text-white transition-colors duration-300"
              data-testid="home-about-readmore"
            >
              Read the full story <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
        <PillarsGrid />
      </div>
      <div className="mt-14 lg:mt-20 space-y-14">
        <StatsBanner />
        <AboutCTA />
      </div>
    </div>
  </section>
  );
};
